import React from 'react'

interface AnalysisItem {
  has_chatbot: boolean
  services: string[] | null
  url: string
}

interface AnalyzeResult {
  source: string
  success: boolean
  with_chatbot: AnalysisItem[]
  without_chatbot: AnalysisItem[]
}

interface StorageSummaryProps {
  analyzeResult?: AnalyzeResult | null
}

const StorageSummary: React.FC<StorageSummaryProps> = ({ analyzeResult = null }) => {
  // Calculate counts from analysis result
  const withChatbotCount = analyzeResult?.with_chatbot?.length || 0
  const withoutChatbotCount = analyzeResult?.without_chatbot?.length || 0
  const totalCount = withChatbotCount + withoutChatbotCount

  // Calculate percentages for the donut chart
  const withChatbotPercent = totalCount > 0 ? (withChatbotCount / totalCount) * 100 : 0
  const withoutChatbotPercent = totalCount > 0 ? (withoutChatbotCount / totalCount) * 100 : 0

  // Generate conic gradient for donut chart
  const getConicGradient = () => {
    if (totalCount === 0) {
      return 'conic-gradient(#e9ecef 0deg 360deg)'
    }
    const withChatbotDeg = (withChatbotPercent / 100) * 360
    return `conic-gradient(#198754 0deg ${withChatbotDeg}deg, #dc3545 ${withChatbotDeg}deg 360deg)`
  }

  return (
    <div className='card shadow-sm border-0 rounded-4'>
      <div className='card-body p-4'>
        {/* Header */}
        <div className='d-flex justify-content-between align-items-center mb-4'>
          <h5 className='card-title mb-0 fw-semibold'>Analysis Summary</h5>
          <button className='btn btn-link text-secondary p-0'>
            <svg width='20' height='20' fill='currentColor' viewBox='0 0 20 20'>
              <circle cx='10' cy='4' r='1.5' />
              <circle cx='10' cy='10' r='1.5' />
              <circle cx='10' cy='16' r='1.5' />
            </svg>
          </button>
        </div>

        {/* Donut Chart - CSS Based */}
        <div className='position-relative mx-auto mb-4' style={{ width: '180px', height: '180px' }}>
          {/* Outer ring with gradient */}
          <div
            style={{
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              background: getConicGradient(),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {/* Inner white circle to create donut effect */}
            <div
              style={{
                width: '110px',
                height: '110px',
                borderRadius: '50%',
                backgroundColor: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column'
              }}
            >
              <span className='fs-4 fw-bold' style={{ color: '#6366F1' }}>
                {totalCount}
              </span>
              <span className='text-muted small'>Total Sites</span>
            </div>
          </div>
        </div>

        {/* Legend or Empty State */}
        {totalCount > 0 ? (
          <div className='d-flex flex-column gap-3'>
            <div className='d-flex align-items-center justify-content-between'>
              <div className='d-flex align-items-center gap-2'>
                <span
                  className='rounded-circle'
                  style={{
                    width: '10px',
                    height: '10px',
                    backgroundColor: '#198754',
                    display: 'inline-block'
                  }}
                />
                <span className='text-dark small'>With Chatbot</span>
              </div>
              <span className='small fw-medium text-dark'>{withChatbotCount} sites</span>
            </div>
            <div className='d-flex align-items-center justify-content-between'>
              <div className='d-flex align-items-center gap-2'>
                <span
                  className='rounded-circle'
                  style={{
                    width: '10px',
                    height: '10px',
                    backgroundColor: '#dc3545',
                    display: 'inline-block'
                  }}
                />
                <span className='text-dark small'>Without Chatbot</span>
              </div>
              <span className='small fw-medium text-dark'>{withoutChatbotCount} sites</span>
            </div>

            {/* Percentage breakdown */}
            <div className='mt-2 pt-2 border-top'>
              <div className='d-flex justify-content-between mb-2'>
                <span className='text-muted small'>Detection Rate</span>
                <span className='small fw-medium' style={{ color: '#198754' }}>
                  {Math.round(withChatbotPercent)}%
                </span>
              </div>
              <div className='progress' style={{ height: '8px' }}>
                <div
                  className='progress-bar'
                  role='progressbar'
                  style={{
                    width: `${withChatbotPercent}%`,
                    backgroundColor: '#198754'
                  }}
                />
              </div>
            </div>
          </div>
        ) : (
          <div className='text-center py-3'>
            <p className='text-muted small mb-0'>
              No analysis data yet. Enter URLs and click "Update" to start analyzing.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

export default StorageSummary
